import { InboxConversationQuery } from '@website/types/graphqlOperations';
import Linkify from 'react-linkify';
import { DateTime } from 'luxon';
import { Card, Col, Spinner } from 'react-bootstrap';
import Avatar from '../directoryTheme/Avatar';

/**
 * Get message details
 */
export function getMessageDetails({
  message,
  inboxUserID,
}: {
  message: InboxConversationQuery['chatConversation']['messages']['items'][0];
  inboxUserID: string;
}): {
  isAuthor: boolean;
  name: string;
  created: string;
  picture?: string;
  isOptimisticUpdate: boolean;
} {
  const author = message.user?.id || message.host?.id;

  const sendingTeamMember = message.host?.team?.items?.find(
    (t) => t.id === message.hostTeamMemberUserID,
  );

  const name =
    message.user?.givenName ||
    `${
      sendingTeamMember
        ? `${sendingTeamMember.givenName} @ ${message.host?.name}`
        : `${message.host?.name}`
    }`;

  const picture =
    message.user?.picture?.file?.key ??
    message.host?.logo?.file?.key ??
    undefined;
  const isAuthor = author === inboxUserID;
  const created = DateTime.fromISO(message.created).toLocaleString(
    DateTime.DATETIME_MED,
  );

  const isOptimisticUpdate = message.id.startsWith('msg_tmp');

  return { isAuthor, name, created, picture, isOptimisticUpdate };
}

/**
 * Message
 *
 * Shows the text and author information, for an individual message.
 */
export default function Message({
  message,
  inboxUserID,
}: {
  message: InboxConversationQuery['chatConversation']['messages']['items'][0];
  inboxUserID: string;
}) {
  const { isAuthor, name, created, picture, isOptimisticUpdate } =
    getMessageDetails({
      message,
      inboxUserID,
    });

  // Display system messages differently
  if (message.system) {
    return (
      <Col lg={12}>
        <div className="mx-5 mt-4 mb-5 text-center">
          <Card className="d-inline-block py-2 px-3 bg-light border-light text-muted">
            {message.text}
          </Card>
        </div>
      </Col>
    );
  }

  return (
    <Col md="9" xl="7" className={`d-flex mb-3 ${isAuthor ? 'ms-auto' : ''}`}>
      {!isAuthor && (
        <Avatar size="sm" image={picture} defaultImageText={name} alt={name} />
      )}
      <div className={`${isAuthor ? 'me-3' : 'ms-3'} col`}>
        <div
          className={`${
            isAuthor ? 'bg-primary' : 'bg-gray-200'
          } rounded p-4 mb-2`}
        >
          <p
            className={`${isAuthor ? 'text-white' : ''} text-sm mb-0`}
            style={{
              // Show line breaks that the user has input (so we don't need to add <p> tags)
              whiteSpace: 'pre-line',
            }}
          >
            <Linkify
              componentDecorator={(decoratedHref, decoratedText, key) => (
                <a
                  target="_blank"
                  href={decoratedHref}
                  key={key}
                  className={`text-decoration-underline ${
                    isAuthor ? 'text-white' : ''
                  }`}
                  style={{ wordBreak: 'break-all' }}
                  rel="noreferrer"
                >
                  {decoratedText.length > 50
                    ? `${decoratedText.substring(0, 40)}...`
                    : decoratedText}
                </a>
              )}
            >
              {message.text}
            </Linkify>
          </p>
        </div>
        <div className="ms-3 small text-muted">
          <p className="my-0">{name}</p>
          <p className="my-0 mb-md-3">{created}</p>
        </div>
      </div>
      {isAuthor && !isOptimisticUpdate && (
        <Avatar size="sm" image={picture} defaultImageText={name} alt={name} />
      )}
      {isOptimisticUpdate && (
        <Spinner animation="border" style={{ margin: 4 }} />
      )}
    </Col>
  );
}
